import React, { useState, useEffect, useContext } from "react";
import * as Constants from "../../constants";
import { ShareContext } from "../../context/ShareContext";
import BackLoader from "../../components/backloader";
import JisEmptyList from "../../components/controls/emptylist";
import NjcReportFilters from "../../components/reports/NjcReportFilters";
import NjcReport3CTable from "../../components/reports/NjcReport3CTable";
import NjcReport4Table from "../../components/reports/NjcReport4Table";
import NjcReport5Table from "../../components/reports/NjcReport5Table";
import { REPORT_TYPES } from "../../components/reports/njcReportConfig";
import Pagenated from "../../components/pagenated";
import wordicon from "../../assets/ms_word.png";
import excelicon from "../../assets/ms_excel.png";
import pdficon from "../../assets/pdf.png";

// Format a Date as YYYY-MM-DD using LOCAL date parts.
// (toISOString() converts to UTC first, which in Lagos (UTC+1) turns
//  31 Mar 00:00 into "2026-03-30" and 1 Jan into the previous 31 Dec.)
const toYmd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
function Report_Njc_3c() {
  const { token, profile, frequentData, setIsExpired } =
    useContext(ShareContext);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState(null);
  const [reportHeader, setReportHeader] = useState(null); // NJC/MCPJ/5 form header
  // filters used for the report currently shown (print/export use these, not the live dropdowns)
  const [generatedSearch, setGeneratedSearch] = useState(null);
  const [search, setSearch] = useState({
    reportType: "",
    eventtype: "all",
    divisionid: "",
    judgeid: "",
    lastdate: "",
    year: "",
  });
  const [err, setErr] = useState("");
  const [courts, setCourts] = useState([]);
  const [count, setCount] = useState(0);
  const [quarters, setQuarters] = useState([]);
  const [pageSize, setPageSize] = useState(Constants.PAGESIZE);
  const [currentPage, setCurrentPage] = useState(1);
  const onPagePick = (p) => {
    console.log("Page picked:", p);
    setCurrentPage(p.id);
  };
  console.log(profile);
  // Anyone who is not restricted to their own cases (pull type "specific", i.e. a judge)
  // can pick a judge: Admin, Chief Judge, assignment judges, report officers...
  const roleName = profile?.role?.name?.toLowerCase() || "";
  const canSelectJudge =
    profile?.role?.pullType !== "specific" ||
    roleName.includes("chief") ||
    roleName.includes("admin");

  // NJC returns start from the 4th quarter of 2013 (after data migration)
  const FIRST_YEAR = 2013;
  const FIRST_QUARTER_START_MONTH = 9; // October

  useEffect(() => {
    onLoadRoles();
    setQuarters(getQuarterArray(FIRST_YEAR, FIRST_QUARTER_START_MONTH));
    if (canSelectJudge) {
      onLoadJudges();
    }
  }, []);

  useEffect(() => {
    if (search.divisionid > 0) {
      onLoadJudges(search.divisionid);
    }
  }, [search.divisionid]);

  useEffect(() => {
    // Update quarters when year changes
    if (search.year) {
      const selectedYear = parseInt(search.year);
      const now = new Date();
      const currentYear = now.getFullYear();

      // If selected year is current year, generate quarters up to current quarter
      // Otherwise, generate all 4 quarters for that year
      const yearQuarters = getQuartersForYear(selectedYear, currentYear);
      setQuarters(yearQuarters);
    } else {
      // Reset to all quarters if no year selected
      setQuarters(getQuarterArray(FIRST_YEAR, FIRST_QUARTER_START_MONTH));
    }
  }, [search.year]);

  const onSearchChange = (e) => {
    const next = { ...search, [e.target.name]: e.target.value };
    // a new year gives a new quarter list: clear the old quarter so a stale one is never sent
    if (e.target.name === "year") next.lastdate = "";
    // a new division gives a new judge list
    if (e.target.name === "divisionid" && canSelectJudge) next.judgeid = "";
    setSearch(next);
  };

  const onSearch = async (sear) => {
    if (!sear.reportType) {
      setErr("Please select a report type");
      return;
    }

    // If not chief judge, use logged-in user's profile ID as judgeid
    const judgeId = canSelectJudge ? sear.judgeid : profile?.id;

    if (!sear.lastdate) {
      setErr("Please select a quarter");
      return;
    }
    // NJC/MCPJ/3C is per division and per judge: the API needs both
    if (sear.reportType === "NJC/MCPJ/3C") {
      if (!sear.divisionid) {
        setErr("Please select a division");
        return;
      }
      if (!judgeId) {
        setErr("Please select a judge");
        return;
      }
    }

    // Update search state with the resolved judgeId
    const updatedSearch = { ...sear, judgeid: judgeId };
    setSearch(updatedSearch);
    setLoading(true);
    setErr("");

    try {
      let endpoint = "";
      let requestBody = null;
      let method = "GET";

      // Calculate fromDate and toDate based on selected quarter
      let fromDate = "";
      let toDate = "";

      if (sear.lastdate) {
        // toDate is the selected quarter end date (already in YYYY-MM-DD format)
        toDate = sear.lastdate;

        // Calculate fromDate as the first day of the quarter (parse parts, no timezone shift)
        const [y, m] = sear.lastdate.split("-").map(Number); // m is 1-12
        const startMonth = Math.floor((m - 1) / 3) * 3; // 0,3,6,9
        fromDate = toYmd(new Date(y, startMonth, 1));
      }

      // Determine endpoint based on report type
      if (sear.reportType === "NJC/MCPJ/3C") {
        // Existing endpoint for NJC/MCPJ/3C
        endpoint = `${Constants.apiUrl}/report/njc-3/${sear.eventtype}/${sear.divisionid}/${judgeId}/${sear.lastdate}`;
        method = "GET";
      } else if (
        sear.reportType === "NJC/MCPJ/4" ||
        sear.reportType === "NJC/MCPJ/4_INDV"
      ) {
        // Contested judgement cases endpoint
        endpoint = `${Constants.apiUrl}/Report/contested-judgement-cases/0/1000`;
        method = "POST";
        requestBody = {
          judgeId: judgeId ? Number(judgeId) : null,
          divisionId: sear.divisionid ? Number(sear.divisionid) : null,
          fromDate: fromDate,
          toDate: toDate,
          eventType: sear.eventtype === "all" ? "" : sear.eventtype,
        };
      } else if (sear.reportType === "NJC/MCPJ/5") {
        // Judgement pending cases endpoint
        endpoint = `${Constants.apiUrl}/Report/judgement-pending-cases/0/0`; // 0 = all rows
        method = "POST";
        requestBody = {
          judgeId: judgeId ? Number(judgeId) : null,
          divisionId: sear.divisionid ? Number(sear.divisionid) : null,
          fromDate: fromDate,
          toDate: toDate,
          eventType: sear.eventtype === "all" ? "" : sear.eventtype,
        };
      }

      const requestOptions = {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      if (requestBody) {
        requestOptions.body = JSON.stringify(requestBody);
      }

      const resp = await fetch(endpoint, requestOptions);
      const data = await resp.json();
      setLoading(false);
      console.log(data);

      if (data.code === 0) {
        const cdocs = data.items.map((item, index) => ({
          ...item,
          id: index + 1,
        }));
        setCases(cdocs);
        setReportHeader(data.header || null);
        setGeneratedSearch(updatedSearch);
        setCount(cdocs?.length || 0);
        setCurrentPage(1);
        console.log("Report Type:", updatedSearch.reportType);
        setReportType(updatedSearch.reportType);
      } else if (data.code === -10) {
        setIsExpired(true);
      } else {
        setErr(data.status || "Failed to fetch report data");
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
      setErr(Constants.serverErr);
    }
  };

  // Quarter options from (startYear, startMonth) to the end of the current year, newest first
  const getQuarterArray = (startYear, startMonth) => {
    const now = new Date();
    const result = [];
    for (let year = startYear; year <= now.getFullYear(); year++) {
      result.push(...getQuartersForYear(year, now.getFullYear(), year === startYear ? startMonth : 0));
    }
    return result.reverse();
  };

  // All four quarters of a year (the current year included), from Q4 2013 onwards.
  const getQuartersForYear = (selectedYear, currentYear, fromMonth = 0) => {
    const quarters = [
      { startMonth: 0, endMonth: 2, endDay: 31, label: "1ST" },
      { startMonth: 3, endMonth: 5, endDay: 30, label: "2ND" },
      { startMonth: 6, endMonth: 8, endDay: 30, label: "3RD" },
      { startMonth: 9, endMonth: 11, endDay: 31, label: "4TH" },
    ];
    const result = [];
    for (const { endMonth, endDay, label } of quarters) {
      if (endMonth < fromMonth) continue; // before the first reportable quarter
      if (selectedYear === FIRST_YEAR && endMonth < FIRST_QUARTER_START_MONTH) continue;
      result.push({
        value: toYmd(new Date(selectedYear, endMonth, endDay)),
        text: `${label} QUARTER ${selectedYear}`,
      });
    }
    return result;
  };

  const onLoadRoles = async () => {
    // Load roles if needed for future use
    try {
      const resp = await fetch(
        `${Constants.apiUrl}/profile/getrolesBySearch/judge`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await resp.json();
      if (data.code == -10) {
        setIsExpired(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const onLoadJudges = async (divisionId = 0) => {
    try {
      setLoading(true);
      const resp = await fetch(
        `${Constants.apiUrl}/report/njc-judges/${divisionId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await resp.json();
      setLoading(false);
      if (data.code == 0) setCourts(data.list || []);
      if (data.code == 0) {
        //ready
        // fee, feelines, cinfo , parties
      } else if (data.code == -10) {
        setIsExpired(true);
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };
  useEffect(() => {
    setReportType(null);
  }, [search.reportType]);

  // Export the report currently shown (same filters it was generated with) to pdf / excel / word
  const onExport = async (doctype) => {
    const g = generatedSearch;
    if (!g || !g.lastdate) return;
    const [y, m] = g.lastdate.split("-").map(Number);
    const fromDate = toYmd(new Date(y, Math.floor((m - 1) / 3) * 3, 1));
    const ext = { pdf: "pdf", excel: "xlsx", msword: "docx" }[doctype];
    setLoading(true);
    setErr("");
    try {
      const resp = await fetch(`${Constants.apiUrl}/report/njc-export/${doctype}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          reportType: g.reportType,
          eventType: g.eventtype || "all",
          divisionId: g.divisionid ? Number(g.divisionid) : null,
          judgeId: g.judgeid ? Number(g.judgeid) : null,
          fromDate,
          toDate: g.lastdate,
        }),
      });
      if (resp.status === 409) {
        setIsExpired(true);
        return;
      }
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d.status || "Export failed");
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${g.reportType.replace(/[^a-z0-9]+/gi, "-")}-${g.lastdate}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setErr(e.message || "Unable to export the report. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const renderReportTable = () => {
    if (!reportType || cases?.length === 0) return null;
    console.log(reportType, "inside the renderReportTable");

    switch (reportType) {
      case "NJC/MCPJ/3C":
        return (
          <NjcReport3CTable
            cases={cases.slice(
              pageSize * (currentPage - 1),
              pageSize * currentPage
            )}
            allCases={cases}
            search={generatedSearch || search}
            count={count}
          />
        );
      case "NJC/MCPJ/4":
      case "NJC/MCPJ/4_INDV":
        return (
          <NjcReport4Table
            cases={cases.slice(
              pageSize * (currentPage - 1),
              pageSize * currentPage
            )}
            search={generatedSearch || search}
            count={count}
          />
        );
      case "NJC/MCPJ/5":
        return (
          <NjcReport5Table
            cases={cases.slice(
              pageSize * (currentPage - 1),
              pageSize * currentPage
            )}
            search={generatedSearch || search}
            count={count}
            header={reportHeader}
            startIndex={pageSize * (currentPage - 1)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <NjcReportFilters
        search={search}
        onSearchChange={onSearchChange}
        onSearch={onSearch}
        frequentData={frequentData}
        courts={courts}
        quarters={quarters}
        reportTypes={REPORT_TYPES}
        showJudges={canSelectJudge}
      />
      {err && <div className="error-message">{err}</div>}
      {!search.reportType && cases?.length === 0 && (
        <JisEmptyList message="Select a report type and generate report" />
      )}
      {search.reportType && cases?.length === 0 && !loading && (
        <JisEmptyList message="No data available for the selected criteria" />
      )}
      {cases?.length > 0 && reportType && (
        <div className="exportdiv no-print" style={{ display: "flex", gap: 8, justifyContent: "flex-end", margin: "8px 0" }}>
          <img src={wordicon} alt="Word" title="Download Word" style={{ cursor: "pointer" }} onClick={() => onExport("msword")} />
          <img src={excelicon} alt="Excel" title="Download Excel" style={{ cursor: "pointer" }} onClick={() => onExport("excel")} />
          <img src={pdficon} alt="PDF" title="Download PDF" style={{ cursor: "pointer" }} onClick={() => onExport("pdf")} />
        </div>
      )}
      {renderReportTable()}
      {cases?.length > 0 && reportType && (
        <Pagenated
          onChangePageSize={(k) => {
            setPageSize(k);
            setCurrentPage(1);
          }}
          size={pageSize}
          total={count}
          maxPage={20}
          onPick={onPagePick}
          pageNo={currentPage}
        />
      )}
      <BackLoader open={loading} />
    </div>
  );
}

export default Report_Njc_3c;
